import { forwardRef, useMemo, useState } from 'react';

import { styled } from '@spazious/dollhouse-theme';
import { useEventCallback } from '@spazious/react-hooks';

import { InputBase, InputBaseElement, InputBaseProps } from '../InputBase/index.js';

export const getFractionDigits = (step: number | string): number =>
  (step.toString().split('.')[1] ?? '').length;

export const defaultFormatNumber = (value: number, locale: string, fractionDigits = 0): string =>
  value.toLocaleString(locale, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  });

export const castToNumber = (value: any): number => {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export type InputNumberElement = InputBaseElement;
export interface InputNumberProps extends Omit<InputBaseProps, 'type' | 'pattern'> {
  locale?: string;
  format?: (formattedValue: number | string) => string | number;
}

export const InputNumber = styled(
  forwardRef<InputNumberElement, InputNumberProps>(
    ({ value, format, size, defaultValue, onChange, onFocus, onBlur, ...restProps }, ref) => {
      const formattedValue = useMemo(
        () => (value !== undefined ? format?.(castToNumber(value ?? defaultValue)) : ''),
        [defaultValue, format, value]
      );
      const [inputValue, setInputValue] = useState(() => formattedValue);
      const [inputFocused, setInputFocused] = useState(false);

      const handleInputChange = useEventCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(evt.target.value);
        if (evt.target.value) {
          onChange?.(evt);
        }
      });

      const handleInputFocus = useEventCallback((evt: React.FocusEvent<HTMLInputElement>) => {
        setInputFocused(true);
        setInputValue(formattedValue);
        onFocus?.(evt);
      });

      const handleInputBlur = useEventCallback((evt: React.FocusEvent<HTMLInputElement>) => {
        setInputFocused(false);
        setInputValue(formattedValue);
        onBlur?.(evt);
      });

      return (
        <InputBase
          ref={ref}
          inputMode="decimal"
          size={size as any}
          type="text"
          value={inputFocused ? inputValue : formattedValue}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          {...restProps}
        />
      );
    }
  )
).withConfig({
  displayName: 'InputNumber',
})`
  max-width: 120px;
`;
